import React, { useState, useEffect } from "react";
import { Container, Button, Label, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import BaseUrl from "../../base-api";
import Select from "react-select";
import { toast } from "react-toastify";
import { submitPaymentForm } from "../../Utils/Commons";

const UpgradeCourse = () => {
  const navigate = useNavigate();
  const [parentCourses, setParentCourses] = useState([]);
  const [childCourses, setChildCourses] = useState([]);
  const [selectedParent, setSelectedParent] = useState("");
  const [selectedChild, setSelectedChild] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  // Get the selected parent course object to access minCount and maxCount
  const selectedParentCourse = parentCourses.find(course => course.course === selectedParent);
  const isMultiSelect = selectedParentCourse && selectedParentCourse.maxCount > 1;
  const maxSelectCount = selectedParentCourse ? selectedParentCourse.maxCount : 1;
  const hasUpgraded = selectedParentCourse ? selectedParentCourse.hasUpgraded : false;

  useEffect(() => {
    axios
      .get(`${BaseUrl}/app/getParentCourses`, {
        headers: { Authorization: localStorage.getItem("$3ingT0k3n") },
      })
      .then((res) => {
        setParentCourses(res.data);
        if (res.data?.length > 0) setSelectedParent(res.data[0].course);
      })
      .catch((error) => {
        // Handle error silently
      });
  }, []);

  useEffect(() => {
    if (!selectedParent) return;
    setLoading(true);
    setSelectedChild(null); // Reset child when parent changes
    axios
      .get(`${BaseUrl}/app/getAvailableUpgradeCourses`, {
        headers: { Authorization: localStorage.getItem("$3ingT0k3n") },
        params: { parentCourse: selectedParent },
      })
      .then((res) => {
        setChildCourses(res.data); // ["Certificate in Java", ...]
        setLoading(false);
      })
      .catch((err) => {
        setChildCourses([]);
        setLoading(false);
      });
  }, [selectedParent]);

  const handleParentChange = (e) => {
    setSelectedParent(e.target.value);
  };

  const handleChildChange = (option) => {
    if (isMultiSelect && Array.isArray(option)) {
      if (option.length > maxSelectCount) {
        // Trim to max allowed and show warning
        setSelectedChild(option.slice(0, maxSelectCount)?.value);
        toast.warning(`Maximum ${maxSelectCount} courses allowed.`, {
          position: "top-center",
        });
      } else {
        // Allow selection (even if below minimum)
        setSelectedChild(option);
        // Close dropdown when maximum courses are selected
        if (option.length === maxSelectCount) {
          setMenuIsOpen(false);
        }
      }
    } else {
      setSelectedChild(option);
    }
  };

  const handlePreview = () => {
    if (!selectedParent || !selectedChild) return;

    // Check minimum course requirement for multi-select
    if (isMultiSelect && Array.isArray(selectedChild) && selectedChild.length < selectedParentCourse?.minCount) {
      toast.error(`Minimum ${selectedParentCourse?.minCount} courses required for this course. Currently selected: ${selectedChild.length}.`, {
        position: "top-center",
      });
      return;
    }

    const requestData = {
      parentCourse: selectedParent,
      selectedCourses: isMultiSelect
        ? selectedChild.map((c) => c.value)
        : [selectedChild.value],
    };

    setPreviewLoading(true);
    axios
      .post(`${BaseUrl}/app/upgradePreview`, requestData, {
        headers: {
          Authorization: localStorage.getItem("$3ingT0k3n"),
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        setPreviewData(res.data);
        setPreviewModal(true);
        setPreviewLoading(false);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Failed to load preview. Please try again.", {
          position: "top-center",
        });
        setPreviewLoading(false);
      });
  };

  const handleUpgradeHistory = () => {
    setHistoryLoading(true);
    axios
      .get(`${BaseUrl}/app/upgradeHistory`, {
        headers: {
          Authorization: localStorage.getItem("$3ingT0k3n"),
        },
      })
      .then((res) => {
        setHistoryData(res.data);
        setHistoryModal(true);
        setHistoryLoading(false);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Failed to load upgrade history. Please try again.", {
          position: "top-center",
        });
        setHistoryLoading(false);
      });
  };

  // Group upgrade history by parent course, previous course, and date
  const groupHistoryByParentCourse = (historyData) => {
    const grouped = {};

    // Sort history data by timestamp (newest first)
    const sortedData = [...historyData].sort((a, b) =>
      new Date(b.upgradeDate) - new Date(a.upgradeDate)
    );

    // Find the latest upgrade timestamp and its previous course name
    const latestTimestamp = sortedData[0]?.upgradeDate;
    const latestPreviousCourseName = sortedData[0]?.previousCourseName;

    sortedData.forEach(record => {
      // Normalize date to just the date part (remove time component)
      const normalizedDate = new Date(record.upgradeDate).toDateString();
      const key = `${record.parentCourseName}-${record.previousCourseName}-${normalizedDate}`;

      // Determine if this is the latest upgrade
      const isLatestUpgrade = record.upgradeDate === latestTimestamp;

      if (!grouped[key]) {
        grouped[key] = {
          parentCourseName: record.parentCourseName,
          previousCourseName: record.previousCourseName,
          upgradeDate: record.upgradeDate,
          courseNames: [],
          active: record.active,
          paymentReference: record.paymentReference,
          mappingType: record.mappingType,
          isLatestUpgrade: isLatestUpgrade,
          latestPreviousCourseName: latestPreviousCourseName
        };
      }
      grouped[key].courseNames.push(record.courseName);
    });

    return Object.values(grouped);
  };

  const handleUpgrade = () => {
    if (!selectedParent || !selectedChild) return;

    const requestData = {
      parentCourse: selectedParent,
      selectedCourses: isMultiSelect
        ? selectedChild.map((c) => c.value)
        : [selectedChild.value],
    };

    axios
      .post(`${BaseUrl}/app/upgradeCourse`, requestData, {
        headers: {
          Authorization: localStorage.getItem("$3ingT0k3n"),
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        if (res.data.paymentUrl) {
          toast.success("Redirecting to payment gateway...", { position: "top-center" });
          submitPaymentForm(res.data);
        } else if (typeof res.data === "string") {
          toast.error(res.data, { position: "top-center" });
        } else {
          toast.success(res.data, { position: "top-center" });
          navigate("/SideBar/my-courses");
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Upgrade failed. Please try again.", {
          position: "top-center",
        });
      });
  };

  const childOptions = childCourses.map((course) => ({
    value: course.key || course,
    label: course.name || course
  }));

  const isUpgradeDisabled =
    !selectedParent ||
    !selectedChild ||
    (isMultiSelect && Array.isArray(selectedChild) && selectedChild.length === 0) ||
    (isMultiSelect && Array.isArray(selectedChild) && selectedChild.length > selectedParentCourse?.maxCount);

  // Get selection status for display
  const getSelectionStatus = () => {
    if (!selectedParent || !selectedChild) return { text: "Select courses", color: "secondary" };

    if (isMultiSelect && Array.isArray(selectedChild)) {
      const count = selectedChild.length;
      if (count > selectedParentCourse?.maxCount) {
        return { text: `${count}/${selectedParentCourse?.maxCount} (max exceeded)`, color: "danger" };
      } else {
        return { text: `${count}/${selectedParentCourse?.maxCount}`, color: "success" };
      }
    } else {
      return { text: "Selected", color: "success" };
    }
  };

  return (
    <div>
      <Container
        className="py-3 border round mt-3"
        style={{
          width: "100%",
          backgroundColor: "#f6f5f591",
          boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2), 0 6px 20px 0 rgba(0,0,0,0.19)",
          borderRadius: "9px",
        }}
      >

        {/* Course Upgrade Policy */}
        <div className="instructions-list">
          <h3 className="text-center" style={{ color: '#123fae', marginBottom: '20px' }}>
            <i className="fas fa-info-circle mr-2"></i>
            COURSE UPGRADE POLICY
          </h3>
          <ol>
            <li className="mb-2">
              Once you have enrolled in a course, you may also upgrade your course to higher course (HS-CIT (A) to HS-CIT(A+)).
            </li>
            <li>
              However, once the course is upgraded, it will be considered final and cannot be reverted to the previously selected course under any circumstances.
            </li>
          </ol>
        </div>

        <div className="text-center">
          <h1 style={{ backgroundColor: "#4545ee9e", color: "white", borderRadius: "3px", padding: "10px" }}>
            Upgrade Course
          </h1>
        </div>

        {/* Show message when no parent courses are available */}
        {parentCourses.length === 0 && (
          <div className="card border-warning mb-4">
            <div className="card-body text-center">
              <h5 className="card-title text-warning">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                Further upgrade is not allowed
              </h5>
              <p className="card-text">Kindly wait for further updates</p>
              <Link to="/SideBar/my-courses">
                <Button color="secondary">Back to Courses</Button>
              </Link>
            </div>
          </div>
        )}

        {parentCourses.length > 0 && (
          <div>
            <div className="row mt-4">
              {/* Parent Course Dropdown */}
              <div className="col-md-6">
                <div className="form-group">
                  <Label for="parentCourse" className="font-weight-bold">Upgrade Course</Label>
                  <select
                    id="parentCourse"
                    value={selectedParent}
                    onChange={handleParentChange}
                    className="form-control"
                  >
                    <option value="">Select Upgrade Course</option>
                    {parentCourses.map((course) => (
                      <option key={course.course} value={course.course}>{course.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Child Course Dropdown */}
              <div className="col-md-6">
                <div className="form-group">
                  <Label for="childCourse" className="font-weight-bold">
                    {isMultiSelect
                      ? `Select Courses (${selectedParentCourse?.maxCount || 1})`
                      : "Select Course"
                    }
                  </Label>
                  {loading ? (
                    <div>Loading courses...</div>
                  ) : (
                    <Select
                      isMulti={isMultiSelect}
                      id="childCourse"
                      value={selectedChild}
                      onChange={handleChildChange}
                      options={childOptions}
                      isDisabled={!selectedParent || loading}
                      placeholder={isMultiSelect
                        ? `Select  ${selectedParentCourse?.maxCount || 1} courses`
                        : "Select a course"
                      }
                      noOptionsMessage={() => "No courses available"}
                      closeMenuOnSelect={!isMultiSelect}
                      menuIsOpen={menuIsOpen}
                      onMenuOpen={() => setMenuIsOpen(true)}
                      onMenuClose={() => setMenuIsOpen(false)}
                    />
                  )}
                  {!loading && childCourses.length === 0 && selectedParent && (
                    <div className="text-muted mt-1">No courses available for this parent course</div>
                  )}
                </div>
              </div>
            </div>

            <div className="text-center mt-4">
              <Button color="success" onClick={handlePreview} disabled={isUpgradeDisabled} className="mr-2">
                {previewLoading ? "Loading Preview..." : "Preview Upgrade"}
              </Button>
              {hasUpgraded && (
                <Button color="primary" onClick={handleUpgradeHistory} disabled={historyLoading} className="ml-2">
                  {historyLoading ? "Loading..." : "Upgrade History"}
                </Button>
              )}
              <Link to="/SideBar/my-courses">
                <Button color="secondary" className="ml-2">Back to Courses</Button>
              </Link>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        <Modal isOpen={previewModal} toggle={() => setPreviewModal(!previewModal)} size="lg">
          <ModalHeader toggle={() => setPreviewModal(!previewModal)}>Upgrade Preview</ModalHeader>
          <ModalBody>
            {previewData && (
              <div>
                <div className="mb-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="flex-grow-1">
                      <h5 className="mb-1"><strong>Current Course:</strong></h5>
                      <p className="mb-0">{previewData.currentCourse}</p>
                    </div>
                    <div className="mx-5">
                      <span style={{ fontSize: '1.5rem', color: '#007bff', fontWeight: 'bold' }}>→</span>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="mb-1"><strong>Next Upgrade Course:</strong></h5>
                      <p className="mb-0">
                        {(() => {
                          const parentCourseObj = parentCourses.find(c => c.course === previewData.parentCourse);
                          return parentCourseObj ? parentCourseObj.name : previewData.parentCourse;
                        })()}
                      </p>
                      <div className="mt-2">
                        <h6 className="mb-2"><strong>Selected Courses:</strong></h6>
                        <ul className="list-unstyled mb-0" style={{ fontSize: '0.9rem' }}>
                          {previewData.selectedCourses?.map((course, index) => {
                            const courseObj = childCourses.find(c => (c.key || c) === course);
                            return (
                              <li key={index} className="mb-1">
                                {courseObj ? courseObj.name : course}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                {previewData.pricing && (
                  <div className="mb-3">
                    <h5>Pricing Details:</h5>
                    <p><strong>Total Amount:</strong> {previewData.pricing.totalAmount}</p>
                    {previewData.pricing.discount && (
                      <p><strong>Discount:</strong> {previewData.pricing.discount}</p>
                    )}
                    {previewData.pricing.finalAmount && (
                      <p><strong>Final Amount:</strong> {previewData.pricing.finalAmount}</p>
                    )}
                  </div>
                )}
                {previewData.additionalInfo && (
                  <div className="mb-3">
                    <h5>Additional Information:</h5>
                    <p>{previewData.additionalInfo}</p>
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="success" onClick={() => {
              setPreviewModal(!previewModal);
              handleUpgrade();
            }}>Proceed with Upgrade</Button>
          </ModalFooter>
        </Modal>
        {/* Upgrade History Modal */}
        <Modal isOpen={historyModal} toggle={() => setHistoryModal(!historyModal)} size="lg">
          <ModalHeader toggle={() => setHistoryModal(!historyModal)}>Upgrade History</ModalHeader>
          <ModalBody>
            {historyData && (
              <div>
                {historyData.length > 0 ? (
                  <div>
                    {groupHistoryByParentCourse(historyData).map((group, index) => (
                      <div key={index} className="mb-4 p-3 border rounded">
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <p><strong>Date:</strong> {new Date(group.upgradeDate).toLocaleDateString()}</p>
                          </div>
                          <div className="col-md-6 text-right">
                            <p><strong>Status:</strong>
                              <span className={`ml-2 badge ${group.active = 'badge-success'
                                }`}>
                                {group.active = 'Completed'}
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-12">
                            <p><strong>Upgrade Details:</strong></p>
                            <div className="ml-3">
                              {group.isLatestUpgrade ? (
                                // Latest upgrade: show from previousCourseName to parentCourseName
                                <div className="row">
                                  <div className="col-md-4">
                                    <p className="mb-1"><strong>From:</strong></p>
                                    <p>{group.previousCourseName}</p>
                                  </div>
                                  <div className="col-md-1 text-center">
                                    <span style={{ fontSize: '1.5rem', color: '#007bff', fontWeight: 'bold', margin: '0 15px' }}>→</span>
                                  </div>
                                  <div className="col-md-7">
                                    <p className="mb-1"><strong>To:</strong></p>
                                    <p><strong>{group.parentCourseName}</strong></p>
                                    <div className="mt-2">
                                      <p className="mb-1"><strong>Selected Courses:</strong></p>
                                      <ul className="list-unstyled mb-0" style={{ fontSize: '0.9rem' }}>
                                        {group.courseNames.map((courseName, courseIndex) => (
                                          <li key={courseIndex} className="mb-1">• {courseName}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                // Older upgrade: show from previousCourseName to the previousCourseName of latest upgrade
                                <div className="row">
                                  <div className="col-md-4">
                                    <p className="mb-1"><strong>From:</strong></p>
                                    <p>{group.previousCourseName}</p>
                                  </div>
                                  <div className="col-md-1 text-center">
                                    <span style={{ fontSize: '1.5rem', color: '#007bff', fontWeight: 'bold', margin: '0 15px' }}>→</span>
                                  </div>
                                  <div className="col-md-7">
                                    <p className="mb-1"><strong>To:</strong></p>
                                    <p><strong>{group.latestPreviousCourseName}</strong></p>
                                    <div className="mt-2">
                                      <p className="mb-1"><strong>Selected Courses:</strong></p>
                                      <ul className="list-unstyled mb-0" style={{ fontSize: '0.9rem' }}>
                                        {group.courseNames.map((courseName, courseIndex) => (
                                          <li key={courseIndex} className="mb-1">• {courseName}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {group.paymentReference && (
                          <div className="row mt-2">
                            <div className="col-md-12">
                              <p><strong>Payment Reference:</strong> {group.paymentReference}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted">
                    <p>No upgrade history found.</p>
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setHistoryModal(!historyModal)}>Close</Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default UpgradeCourse;